import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Container, Box, Modal,
  Stack, TextField, MenuItem, IconButton, Alert,
  Drawer, List, ListItemButton, ListItemText, Divider,
  useMediaQuery, useTheme
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../utils/serviceName';
import { getCurrentUser, logout } from '../services/auth';
import { api } from '../api';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- стан для модалки ---
  const [inviteOpen, setInviteOpen] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copySuccess, setCopySuccess] = useState('');

  // --- Завантажити користувача при монтуванні ---
  useEffect(() => {
    let ignore = false;
    async function fetchUser() {
      setLoadingUser(true);
      try {
        const data = await getCurrentUser();
        if (!ignore) setUser(data);
      } catch {
        if (!ignore) setUser(null);
      }
      setLoadingUser(false);
    }
    fetchUser();
    return () => { ignore = true; };
  }, []);

  // --- Відкрити модалку і підвантажити послуги ---
  const handleOpenInvite = async () => {
    setInviteOpen(true);
    setInviteLink('');
    setCopySuccess('');
    setSelectedServiceId('');
    try {
      if (user && user.id) {
        const services = await api.getServices(user.id);
        setAllServices(services);
      }
    } catch {
      setAllServices([]);
    }
  };

  const handleGenerateLink = async () => {
    setInviteLink('');
    setCopySuccess('');
    try {
      const res = await api.createBookingLink({
        masterId: user.id,
        serviceId: selectedServiceId || undefined,
      });
      setInviteLink(res.url);
    } catch (e) {
      setInviteLink(t('invite.error'));
    }
  };

  const handleCopy = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopySuccess(t('common.copied'));
    setTimeout(() => setCopySuccess(''), 1500);
  };

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  // --- Пункти навігації за роллю ---
  const navItems = [];
  if (user?.role === 'user') {
    navItems.push({ label: t('nav.cabinet'), to: '/user/cabinet' });
    navItems.push({ label: t('nav.book'), to: '/book' });
  }
  if (user?.role === 'master') {
    navItems.push({ label: t('nav.generateLink'), onClick: handleOpenInvite });
    navItems.push({ label: t('nav.panel'), to: '/master/dashboard', resetWeek: true });
    navItems.push({ label: t('nav.clients'), to: '/master/clients' });
    navItems.push({ label: t('nav.promos'), to: '/master/promos' });
    navItems.push({ label: t('nav.schedule'), to: '/master/schedule' });
    navItems.push({ label: t('nav.profile'), to: '/master/services' });
    navItems.push({ label: t('nav.accounting'), to: '/master/accounting' });
  }
  if (user) {
    navItems.push({ label: t('nav.logout'), onClick: handleLogout });
  } else {
    // Гість теж має пряму кнопку запису (бронювання без акаунта)
    navItems.push({ label: t('nav.book'), to: '/book' });
    navItems.push({ label: t('nav.login'), to: '/login' });
    navItems.push({ label: t('nav.register'), to: '/register' });
  }

  const handleNav = (item) => {
    setDrawerOpen(false);
    if (item.onClick) item.onClick();
    else if (item.to) {
      if (item.resetWeek) {
        // Явний перехід у «Панель» — завжди поточний тиждень (скидаємо збережений)
        sessionStorage.setItem('dashWeekOffset', '0');
        navigate(item.to, { state: { resetWeek: true } });
      } else {
        navigate(item.to);
      }
    }
  };

  // --- Поки користувач не завантажився, показувати лоадер/плейсхолдер ---
  if (loadingUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <AppBar position="static">
        <Toolbar sx={{ gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Link
              to="/"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#fff', textDecoration: 'none' }}
            >
              <Box
                component="img"
                src="/logo-chc.png"
                alt="Clean Hands Club"
                sx={{ height: { xs: 30, md: 36 }, width: 'auto', display: 'block' }}
              />
              <Typography
                variant="h6"
                noWrap
                sx={{ fontSize: { xs: '0.95rem', md: '1.15rem' }, letterSpacing: '0.02em' }}
              >
                Clean Hands Club
              </Typography>
            </Link>
          </Box>

          {/* Десктоп: інлайн-навігація */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {navItems.map((item) => (
              <Button key={item.label} color="inherit" onClick={() => handleNav(item)}
                sx={{ whiteSpace: 'nowrap' }}>
                {item.label}
              </Button>
            ))}
            <LanguageSwitcher />
          </Box>

          {/* Мобільний: перемикач мов + гамбургер (доступні на будь-якій сторінці) */}
          <Box sx={{ display: { xs: 'inline-flex', md: 'none' }, alignItems: 'center' }}>
            <LanguageSwitcher />
            <IconButton
              color="inherit"
              edge="end"
              aria-label={t('nav.menu')}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Мобільне меню */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
            <Typography variant="h6">{t('nav.menu')}</Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <Divider />
          <List>
            {navItems.map((item) => (
              <ListItemButton key={item.label} onClick={() => handleNav(item)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
      {/* --- Модалка для запрошення клієнта --- */}
      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper', color: 'text.primary',
          border: '1px solid', borderColor: 'divider',
          borderRadius: 2, p: { xs: 3, sm: 4 },
          width: '90%', maxWidth: 400
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('invite.title')}</Typography>
            <IconButton onClick={() => setInviteOpen(false)} size="small" sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography sx={{ mt: 2, mb: 1 }}>{t('invite.pickService')}</Typography>
          <TextField
            select
            label={t('invite.service')}
            value={selectedServiceId}
            onChange={e => setSelectedServiceId(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">{t('invite.anyService')}</MenuItem>
            {allServices.map(s =>
              <MenuItem key={s.id} value={s.id}>{pickServiceName(s, i18n.language)}</MenuItem>
            )}
          </TextField>
          <Button
            variant="contained"
            fullWidth
            onClick={handleGenerateLink}
            sx={{ fontWeight: 600, borderRadius: 2, textTransform: 'none', mb: 2 }}
          >
            {t('invite.generate')}
          </Button>
          {inviteLink && (
            <Stack spacing={1} alignItems="center">
              <TextField
                value={inviteLink}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={handleCopy} size="small">
                      <ContentCopyIcon color="primary" />
                    </IconButton>
                  )
                }}
                fullWidth
                sx={{ mb: 1 }}
              />
              {copySuccess && <Alert severity="success">{copySuccess}</Alert>}
            </Stack>
          )}
        </Box>
      </Modal>
      <Container sx={{ mt: 4 }}>{children}</Container>
    </Box>
  );
}
